import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getPackages } from '../lib/api';

const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await getPackages();
        setPackages(res.data);
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="packages-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Our Offerings</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Packages</h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">
            Choose the perfect package for your event. All packages can be customized to fit your needs.
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 sm:py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {packages.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500">No packages available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={`bg-white border border-neutral-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300 relative ${pkg.is_popular ? 'border-2 border-neutral-900' : ''}`}
                  data-testid={`package-${pkg.id}`}
                >
                  {pkg.is_popular && (
                    <span className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 bg-neutral-900 text-white px-3 sm:px-4 py-1 text-xs font-semibold tracking-wider uppercase">Most Popular</span>
                  )}
                  
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{pkg.name}</h3>
                    <p className="text-3xl sm:text-4xl font-bold text-neutral-900">{pkg.price}</p>
                  </div>
                  
                  <p className="text-sm sm:text-base leading-relaxed text-neutral-500 mb-6 sm:mb-8">{pkg.description}</p>
                  
                  <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 sm:gap-3">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-900 flex-shrink-0 mt-0.5" />
                        <span className="text-neutral-600 text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to="/availability" 
                    className={`block text-center py-3 sm:py-4 font-semibold transition-colors text-sm sm:text-base ${
                      pkg.is_popular 
                        ? 'bg-neutral-900 text-white hover:bg-neutral-800' 
                        : 'border-2 border-neutral-200 hover:border-neutral-900'
                    }`}
                  >
                    Book Now
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Need a Custom Package?</h2>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            We can create a custom package tailored to your specific needs and budget.
          </p>
          <Link to="/contact" className="bg-neutral-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg shadow-neutral-900/20 inline-block text-sm sm:text-base">
            Contact Us
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PackagesPage;
